import { Form, Head } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsNotificationsPath } from "@/routes"

/**
 * OKS-40 — Settings → Notifications page (creator-only).
 *
 * Wire format: `notification_preferences[<event_key>]` = "true" |
 * "false". Server coerces via `ActiveModel::Type::Boolean`.
 *
 * V1 event keys (locked, mirrors `User::NOTIFICATION_EVENT_KEYS`):
 *   - mission_assigned
 *   - mission_deadline_reminder
 *   - content_approved
 *   - content_flagged
 *
 * Default-on semantics: a freshly-seeded user sees every switch ON.
 * Switches save on submit, NOT auto-save (UX Spec §4 — explicit
 * Save matches the rest of Settings; auto-save risks accidental
 * opt-outs).
 */

const EVENT_KEYS = [
  "mission_assigned",
  "mission_deadline_reminder",
  "content_approved",
  "content_flagged",
] as const

type EventKey = (typeof EVENT_KEYS)[number]

export interface SettingsNotificationsProps {
  preferences: Record<EventKey, boolean>
}

export default function SettingsNotifications({
  preferences,
}: SettingsNotificationsProps) {
  const { t } = useTranslation()
  // Local mirror of the toggle state — Switch is uncontrolled
  // for accessibility (parent persists at submit), but we mirror
  // here so the hidden inputs ship the right values.
  const [state, setState] = useState<Record<EventKey, boolean>>(preferences)

  return (
    <AppLayout>
      <Head title={t("settings.creator.notifications.title")} />

      <SettingsLayout>
        <div data-testid="settings-notifications" className="space-y-6">
          <HeadingSmall
            title={t("settings.creator.notifications.title")}
            description={t("settings.creator.notifications.description")}
          />

          <Form
            method="patch"
            action={settingsNotificationsPath()}
            options={{ preserveScroll: true }}
            className="space-y-6"
            data-testid="settings-notifications-form"
          >
            {({ processing }) => (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t("settings.creator.notifications.email_title")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4 text-sm">
                      {t("settings.creator.notifications.in_app_note")}
                    </p>
                    <div className="space-y-4">
                      {EVENT_KEYS.map((key) => (
                        <div
                          key={key}
                          data-testid={`settings-notifications-row-${key}`}
                          className="flex items-start justify-between gap-4"
                        >
                          <div className="flex-1">
                            <Label htmlFor={`notif-${key}`}>
                              {t(
                                `settings.creator.notifications.events.${key}.label`,
                              )}
                            </Label>
                            <p className="text-muted-foreground text-xs">
                              {t(
                                `settings.creator.notifications.events.${key}.helper`,
                              )}
                            </p>
                          </div>
                          <Switch
                            id={`notif-${key}`}
                            checked={state[key]}
                            aria-label={t(
                              `settings.creator.notifications.events.${key}.label`,
                            )}
                            onCheckedChange={(value) =>
                              setState((prev) => ({ ...prev, [key]: value }))
                            }
                            data-testid={`settings-notifications-switch-${key}`}
                          />
                          <input
                            type="hidden"
                            name={`notification_preferences[${key}]`}
                            value={state[key] ? "true" : "false"}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Button
                  type="submit"
                  disabled={processing}
                  data-testid="settings-notifications-save"
                >
                  {processing
                    ? t("settings.creator.notifications.saving")
                    : t("settings.creator.notifications.save")}
                </Button>
              </>
            )}
          </Form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
